export interface S3UploadResult {
  key: string;
  originalName: string;
  contentType: string;
  size: number;
}

/**
 * Upload a file directly to S3 using presigned URL
 * @param file - File to upload
 * @param userId - User ID for organizing files
 * @returns Upload result with S3 key
 */
export async function uploadFileToS3(
  file: File,
  userId: string
): Promise<S3UploadResult> {
  const serverUrl = process.env.REACT_APP_SERVER_URL || "http://localhost:8000";

  try {
    // Step 1: Request presigned URL from backend
    const response = await fetch(`${serverUrl}/api/campaigns/generate-upload-url`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "user-id": userId,
      },
      body: JSON.stringify({
        fileName: file.name,
        fileType: file.type || "application/octet-stream",
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to get upload URL: ${response.statusText}`);
    }

    const { uploadUrl, key } = await response.json();

    // Step 2: Upload file directly to S3 using presigned URL
    const uploadResponse = await fetch(uploadUrl, {
      method: "PUT",
      headers: {
        "Content-Type": file.type || "application/octet-stream",
      },
      body: file,
    });

    if (!uploadResponse.ok) {
      throw new Error(`Failed to upload to S3: ${uploadResponse.statusText}`);
    }

    return {
      key,
      originalName: file.name,
      contentType: file.type || "application/octet-stream",
      size: file.size,
    };
  } catch (error) {
    console.error(`[S3 Upload] Failed to upload ${file.name}:`, error);
    throw new Error(
      `Failed to upload ${file.name} to S3: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
  }
}
